// AWS Signature Version 4 for a single S3 PutObject from the browser. Replaces
// the ~440 KB @aws-sdk/client-s3 dependency for this module's only AWS call.
//
// References:
//   https://docs.aws.amazon.com/AmazonS3/latest/API/sig-v4-header-based-auth.html
//   https://docs.aws.amazon.com/general/latest/gr/sigv4-create-canonical-request.html
//
// Scope: PUT one object with a body that fits in memory, using virtual-hosted-style
// URLs (`{bucket}.s3.{region}.amazonaws.com`). Session token from STS travels in
// `x-amz-security-token`. No query string, no checksum trailer.

const SERVICE = 's3';
const ALGORITHM = 'AWS4-HMAC-SHA256';
// Alphabetical, lowercase. Content-Type must be signed because we send it on
// the request — AWS SDK v3 includes it for PutObject and so do we, or S3
// returns SignatureDoesNotMatch.
const SIGNED_HEADERS = 'content-type;host;x-amz-content-sha256;x-amz-date;x-amz-security-token';

const toHex = (buf: ArrayBuffer): string => {
  const bytes = new Uint8Array(buf);
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
};

const sha256 = (data: BufferSource | string): Promise<ArrayBuffer> => {
  const bytes: BufferSource = typeof data === 'string' ? new TextEncoder().encode(data) : data;
  return crypto.subtle.digest('SHA-256', bytes);
};

const sha256Hex = async (data: BufferSource | string): Promise<string> =>
  toHex(await sha256(data));

const hmac = async (key: BufferSource, data: string): Promise<ArrayBuffer> => {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  return crypto.subtle.sign('HMAC', cryptoKey, new TextEncoder().encode(data));
};

// S3 canonical URI: percent-encode each path segment per RFC 3986, keeping `/`
// as the segment separator. encodeURIComponent matches the RFC 3986 unreserved
// set after we apply it segment-by-segment.
const encodeS3Key = (key: string): string =>
  key.split('/').map(encodeURIComponent).join('/');

export interface SignParams {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  region: string;
  bucket: string;
  key: string;
  body: BufferSource;
  contentType: string;
  date?: Date; // testable injection point
}

export interface SignedRequest {
  url: string;
  headers: Record<string, string>;
}

export const signPut = async (p: SignParams): Promise<SignedRequest> => {
  const host = `${p.bucket}.s3.${p.region}.amazonaws.com`;
  const encodedKey = encodeS3Key(p.key);
  const url = `https://${host}/${encodedKey}`;

  const now = p.date ?? new Date();
  // YYYYMMDDTHHMMSSZ
  const amzDate = now.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  // YYYYMMDD
  const dateYmd = amzDate.slice(0, 8);
  const payloadHash = await sha256Hex(p.body);

  // Canonical headers must be sorted alphabetically by name and lowercased.
  // `c` < `h` < `x`, so content-type comes first.
  const canonicalHeaders =
    `content-type:${p.contentType}\n` +
    `host:${host}\n` +
    `x-amz-content-sha256:${payloadHash}\n` +
    `x-amz-date:${amzDate}\n` +
    `x-amz-security-token:${p.sessionToken}\n`;

  const canonicalRequest =
    'PUT\n' +
    `/${encodedKey}\n` +
    '\n' + // empty query string
    canonicalHeaders +
    '\n' +
    SIGNED_HEADERS + '\n' +
    payloadHash;

  const credentialScope = `${dateYmd}/${p.region}/${SERVICE}/aws4_request`;
  const stringToSign =
    `${ALGORITHM}\n` +
    `${amzDate}\n` +
    `${credentialScope}\n` +
    (await sha256Hex(canonicalRequest));

  const kDate = await hmac(
    new TextEncoder().encode(`AWS4${p.secretAccessKey}`),
    dateYmd,
  );
  const kRegion = await hmac(kDate, p.region);
  const kService = await hmac(kRegion, SERVICE);
  const kSigning = await hmac(kService, 'aws4_request');
  const signature = toHex(await hmac(kSigning, stringToSign));

  const authorization =
    `${ALGORITHM} ` +
    `Credential=${p.accessKeyId}/${credentialScope}, ` +
    `SignedHeaders=${SIGNED_HEADERS}, ` +
    `Signature=${signature}`;

  return {
    url,
    headers: {
      Authorization: authorization,
      'Content-Type': p.contentType,
      'x-amz-content-sha256': payloadHash,
      'x-amz-date': amzDate,
      'x-amz-security-token': p.sessionToken,
    },
  };
};
